import { useCallback, useMemo, useState } from 'react'

import { useDeleteDocument } from 'bank-documents/src/service/deleteDocument'
import { BeforeFormChangeHandler, useFormData } from 'brief-form'
import { FilterCompanyDocumentsWebPage } from 'commons/service/bank/companyDetails'
import { CompanyDocumentsWebPage, Document } from 'commons/types/DTO/bank/companyDetails/document'
import { Toast } from 'ui/components'
import { useTableState } from 'ui/components/Table'
import { useConfirmation } from 'ui/hooks'

import { handlerServerError } from '@orbit/errors'

export const useComponentsEssence = (
  companyId: number,
  filterCompanyDocumentsWebPage: (filter: FilterCompanyDocumentsWebPage) => void,
  webPage: CompanyDocumentsWebPage
) => {
  const initialFilter = useMemo(
    () => ({
      alerts: '',
      frequency: 'all',
      page: 1,
      size: 10,
      relationshipId: companyId,
      sortBy: '',
      sortDesc: false,
      subjectType: 'all',
      isInternal: null,
      licenseId: 0,
      bankAccountId: 0,
      contactId: 0
    }),
    [companyId]
  )

  const [tableFilter, setTableFilter] = useState<FilterCompanyDocumentsWebPage>(initialFilter)

  const tableState = useTableState({
    filter: {},
    order: [{ field: initialFilter.sortBy, direction: initialFilter.sortDesc ? 'DESC' : 'ASC' }]
  })

  const changePagination = useCallback(
    async (page: number, size: number) => {
      tableState.onPageChange(page, size)

      const filter = {
        ...tableFilter,
        page,
        size
      }

      setTableFilter(filter)

      filterCompanyDocumentsWebPage(filter)
    },
    [filterCompanyDocumentsWebPage, tableFilter, tableState]
  )

  const onFilterChange = useCallback(
    async (value: FilterCompanyDocumentsWebPage) => {
      const filter = {
        ...value,
        page: initialFilter.page,
        size: tableState.size
      }

      tableState.onPageChange(initialFilter.page, tableState.size)
      tableState.filterForm?.set({ value })

      setTableFilter(filter)
      filterCompanyDocumentsWebPage(filter)
    },
    [filterCompanyDocumentsWebPage, tableState]
  )

  const onBeforeChange: BeforeFormChangeHandler<FilterCompanyDocumentsWebPage> = useCallback(
    ({ value, errors }) => {
      if (value.subjectType !== tableFilter.subjectType) {
        value.bankAccountId = initialFilter.bankAccountId
        value.contactId = initialFilter.contactId
        value.licenseId = initialFilter.licenseId
      }

      onFilterChange(value)
      return {
        value,
        errors
      }
    },
    [onFilterChange, tableFilter, initialFilter]
  )

  const filterForm = useFormData<FilterCompanyDocumentsWebPage>({
    initialValue: initialFilter,
    onBeforeChange
  })

  const clearFilter = useCallback(() => {
    onFilterChange({ relationshipId: companyId, page: initialFilter.page, size: initialFilter.size })
    tableState.onPageChange(initialFilter.page, tableState.size)
    filterForm.set({ reset: true, value: initialFilter })
  }, [onFilterChange, filterForm, initialFilter, tableState, companyId])

  const onSort = useCallback(
    async (direction: 'ASC' | 'DESC', field) => {
      const isDesc = direction === 'DESC'
      tableState.onOrderChange({ field, direction })
      const filter = {
        ...tableFilter,
        page: tableState.page,
        size: tableState.size,
        sortBy: field,
        sortDesc: isDesc
      }

      filterCompanyDocumentsWebPage(filter)
    },
    [filterCompanyDocumentsWebPage, tableState, tableFilter]
  )

  const getCompanyDocumentsAfterDeleteWebPage = () => {
    const isDeletingAnnualReviewLastOnPage = tableFilter.size * tableFilter.page >= webPage.documentsTotalCount

    const page = isDeletingAnnualReviewLastOnPage
      ? Math.ceil((webPage.documentsTotalCount - 1) / tableFilter.size) || 1
      : tableFilter.page

    filterCompanyDocumentsWebPage({
      ...tableFilter,
      page
    })

    tableState.onPageChange(page, tableState.size)

    const filter = {
      ...tableFilter,
      page
    }

    setTableFilter(filter)
  }

  const [documentForDelete, setDocumentForDelete] = useState<Document | undefined>(undefined)
  const { routine: deleteMutation } = useDeleteDocument({
    onSuccess: async () => {
      await getCompanyDocumentsAfterDeleteWebPage()
    },
    onError: (error) => handlerServerError(error)
  })

  const deleteDocument = useCallback(async () => {
    try {
      await deleteMutation(documentForDelete?.id || 0)

      Toast.successSmall(`Document was successfully deleted.`)
    } catch (error) {
      handlerServerError(error)
    }
  }, [deleteMutation, documentForDelete])

  const { Confirmation: DeleteConfirmation, open: openDeleteConfirmation } = useConfirmation({
    message: (
      <>
        Are you sure you want to delete
        <br />
        <i>{documentForDelete?.documentName}</i> document?
      </>
    ),
    onConfirm: deleteDocument,
    confirmationButtonText: 'Delete',
    isConfirmationNegative: true
  })

  const onDeleteDocument = (document: Document | undefined) => {
    setDocumentForDelete(document)
    openDeleteConfirmation()
  }

  return {
    tableState,
    filterForm,
    changePagination,
    onSort,
    onDeleteDocument,
    DeleteConfirmation,
    openDeleteConfirmation,
    deleteDocument,
    clearFilter
  }
}
